import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import Tippy, { useSingleton } from '@tippyjs/react';
import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { type Instance as TippyInstance } from 'tippy.js/index';
import pinMenu from '../../assets/icons/menuPin.svg';
import unpinMenu from '../../assets/icons/menuUnpin.svg';
import { TippyTargetProvider } from '../../components/tippyContext';
import { TOOLTIP_DELAY_GENERAL } from '../../constants';
import { type PromoLinkType } from '../../shared-types';
import type Stylus from '../../stylus/Stylus';
import ToolButton from '../../toolbars/ToolButton';
import { testId } from '../../utils/objectMenu';
import SearchBox from '../../utils/SearchBox';
import { svgToIcon } from '../../utils/svg';
import { tippyPopoverPlugin } from '../../utils/tippyPopoverPlugin';
import IconsTab from './IconsTab';
import ObjectMenuController from './ObjectMenuController';
import styles from './objectMenuStyles.module.css';
import SearchResults from './SearchResults';
import ShapesTab from './ShapesTab';

export interface TabProps {
  onCollapseToggle?: (slug: string, collapsed: boolean) => void;
}

export interface ObjectMenuProps {
  menuTippyRef?: React.MutableRefObject<TippyInstance | undefined>;
}

export interface ObjectMenuSection<T> {
  title: string;
  // Optional React element to render rather than `title`
  titleElement?: () => JSX.Element;
  items: T[];
  sections: ObjectMenuSection<T>[];
  stylus?: Stylus;
  tooltip?: boolean;
  promoLink?: PromoLinkType;
  showNew?: boolean;
  // Selector for this section for promo tooltips to target
  promoTooltipSelector?: string;
  isFaSection?: boolean;
}

export interface Tab<T> {
  name: string;
  tabContent?: React.ReactNode;
  sections: ObjectMenuSection<T>[];
}

const ObjectMenu: React.FC<ObjectMenuProps> = ({ menuTippyRef }) => {
  const controller = useController(ObjectMenuController);
  const whiteboardController = controller.whiteboardController;
  const tippyRef = useRef<TippyInstance>();
  const { backButtonVisible } = controller;
  const { activeTabName, tabs, searchTerm } = controller.state;
  const [menuHeight, setMenuHeight] = useState(0);
  const [tippySingletonSource, tippySingletonTarget] = useSingleton();
  const { objectMenuPinned } = controller.whiteboardController.state;
  const pinIcon = objectMenuPinned ? unpinMenu : pinMenu;

  const activeSections = controller.activeSections();

  useEffect(() => {
    const scrollHandler = event => {
      // Hide the tooltip when the menu is scrolled
      const isPopup = event.target.closest('[data-tippy-root]') !== null;
      if (isPopup && tippyRef.current) {
        tippyRef.current.hide();
      }
    };

    window.addEventListener('wheel', scrollHandler);

    return () => {
      controller.actionClearSearchTerm();
      window.removeEventListener('wheel', scrollHandler);
    };
  }, [controller, controller.whiteboardController.state.currentStylusMenu]);

  const [didScroll, setDidScroll] = useState(false);

  // Reset the scroll position when changing between sections.
  const bodyRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
      setDidScroll(false);
    }
  }, [activeSections, controller.state.searchTerm]);

  useEffect(() => {
    const body = bodyRef.current;
    if (body) {
      const handler = () => {
        setDidScroll(c => c || body.scrollTop > 0);
      };
      body.addEventListener('scroll', handler);
      return () => body.removeEventListener('scroll', handler);
    }
  });

  useEffect(() => {
    const preventPageScrollHandler = (event: WheelEvent) => {
      if (bodyRef.current !== null) {
        const target = event.target as HTMLElement;

        // Only prevent page scroll if inside the menu. This goes against the
        // default browser behavior to continue scrolling a page when the end of
        // a container is reached. However, the whiteboard is essentially its own
        // application within the page, so scrolling past it effectively "exits"
        // the user from the application. This is unexpected within the context
        // of the whiteboard "app", so we prevent it here to provide a better experience.
        const popup = target?.closest('[data-tippy-root]');
        const objectMenu = popup?.querySelector(`.${styles.objectMenu}`);
        if (!popup || !objectMenu) {
          return;
        }

        // Avoid issues with subpixel rendering. Without this, scrollTop is 0.5 off
        const scrollTop = Math.round(bodyRef.current.scrollTop);
        const scrollHeight = Math.round(bodyRef.current.scrollHeight);
        const clientHeight = Math.round(bodyRef.current.clientHeight);
        const atTop = scrollTop === 0;
        const atBottom = scrollTop >= scrollHeight - clientHeight;

        // Prevent the window from scrolling when scrolling within the object menu
        // Reset the menu on close
        if ((event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) {
          event.preventDefault();
        }
      }
    };

    window.addEventListener('wheel', preventPageScrollHandler, {
      passive: false,
    });

    return () => {
      window.removeEventListener('wheel', preventPageScrollHandler);
    };
  }, []);

  // Fit the menu to the canvas. In the event of a whiteboard fullscreen, we have
  // to detect changes to the canvas directly (as opposed to the window)
  useEffect(() => {
    const canvasResizeObserver = new ResizeObserver(() => {
      setMenuHeight(whiteboardController.canvas.getHeight() - 28);
      updateMenuTippy();
    });
    canvasResizeObserver.observe(whiteboardController.canvas.containerElement);
    return () => {
      canvasResizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const mouseDownHandler = () => {
      if (!controller.whiteboardController.state.objectMenuPinned) {
        controller.actionCloseMenu();
      }
    };

    controller.whiteboardController.canvas.on('mouse:down', mouseDownHandler);

    return () => {
      controller.whiteboardController.canvas.off(
        'mouse:down',
        mouseDownHandler
      );
    };
  }, [controller]);

  const Tab = activeTabName === 'Shapes' ? ShapesTab : IconsTab;

  // Fix menu position when content is resized
  const updateMenuTippy = () => {
    // Use tiny delay or it doesn't position it quite right
    setTimeout(
      async () => await menuTippyRef?.current?.popperInstance?.update(),
      1
    );
  };

  return (
    <div
      className={styles.objectMenu}
      style={{ '--object-menu-height': `${menuHeight}px` }}
      data-testid={'Object-menu'}
    >
      <Tippy
        placement='right'
        singleton={tippySingletonSource}
        delay={TOOLTIP_DELAY_GENERAL}
        zIndex={12000}
        plugins={[tippyPopoverPlugin]}
        onShow={instance => {
          tippyRef.current = instance;
        }}
      />
      <TippyTargetProvider value={tippySingletonTarget}>
        <div>
          <div className={styles.objectMenuTopBar}>
            <div className={styles.objectMenuTabs}>
              {tabs.map(tab => (
                <span
                  key={tab.name}
                  className={classNames(styles.objectMenuTab, {
                    [styles.objectMenuTabActive]:
                      activeTabName === tab.name && !searchTerm,
                  })}
                  onClick={() => {
                    controller.actionSetActiveTab(tab.name);
                  }}
                >
                  {tab.name}
                </span>
              ))}
            </div>
            <ToolButton
              name={objectMenuPinned ? 'Unpin menu' : 'Pin menu'}
              onClick={() => {
                controller.whiteboardController.actionSetMenuPinned(
                  !objectMenuPinned
                );
              }}
              testId={testId('pinMenu')}
            >
              {svgToIcon(pinIcon)}
            </ToolButton>
          </div>
          <div className={styles.objectSearchContainer}>
            {backButtonVisible && (
              <div
                className={styles.objectSearchContainerBackButton}
                onClick={() => controller.actionPopSections()}
              >
                <i className='fa-solid fa-arrow-left' />
              </div>
            )}
            <SearchBox
              value={searchTerm}
              style={{ margin: 0 }}
              onChange={(term: string) => {
                controller.actionUpdateSearchResults(term);
                updateMenuTippy();
              }}
            />
          </div>
        </div>
        <div ref={bodyRef} className={styles.objectMenuContentContainer}>
          {searchTerm.length > 0 ? (
            <SearchResults didScroll={didScroll} />
          ) : (
            <Tab onCollapseToggle={updateMenuTippy} />
          )}
        </div>
      </TippyTargetProvider>
    </div>
  );
};

const ControlledObjectMenu = StartControllerScope(
  ObjectMenuController,
  ApplicationView(ObjectMenu)
);

export default ControlledObjectMenu;
